import {
    ButtonGradientPrimary,
    Checkbox,
    Colors,
    Icon,
    MultilineInput,
    Stepper,
    type StepperStep,
    TextInput,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ChevronDownIcon, ChevronUpIcon } from './components/icons';
import {
    CreateProfileReturnMode,
    resolveCreateProfileWizardLocationState,
    resolveCreateProfileWizardReturnPath,
    type CreateProfileWizardLocationState,
} from './createProfileWizardNavigation';

import type { CompetencyGroup } from '@/types/competency/Competency';

import { readRefusal } from '@/api/api';
import { useGetCompetencyGroups } from '@/api/competency-services/competency';
import { useCreateCompetencyProfile } from '@/api/competency-services/competency-profile';
import { useAssignProfile } from '@/api/competency-services/profile-assignment';
import { BackLink } from '@/components/BackLink';
import { ProficiencyLevel } from '@/constants/ProficiencyLevel';
import { useEmbeddedMode } from '@/contexts/EmbeddedModeContext';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import { CharacterCount } from '@/pages/Competencies/components/profiles/CharacterCount';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

const STEP_KEYS = ['stepSelect', 'stepReview'] as const;

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

/**
 * Builds a localized record by setting the value only for the active language.
 */
const buildLocalizedRecord = (language: string, value: string): Record<string, string> => {
    const result: Record<string, string> = { [language]: value };
    return result;
};

export const CompetencyProfileWizardPage = () => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const navigate = useNavigate();
    const history = useHistory();
    const embedded = useEmbeddedMode();
    const location = useLocation<CreateProfileWizardLocationState | undefined>();
    const { handleError } = useError();

    const wizardState = resolveCreateProfileWizardLocationState({
        locationState: location.state,
        search: location.search,
    });
    const returnEmployeeId = wizardState?.returnEmployeeId;
    const returnEmployeeName = wizardState?.returnEmployeeName;

    const { data: competencyGroups } = useGetCompetencyGroups();
    const { mutateAsync: createProfile, isPending } = useCreateCompetencyProfile();
    const { mutateAsync: assignProfile } = useAssignProfile();

    const groups = useMemo(() => competencyGroups ?? [], [competencyGroups]);
    const assignableGroups = useMemo(
        () =>
            groups
                .map((group) => ({
                    ...group,
                    competencies: group.competencies.filter(
                        (competency) => competency.status === CompetencyStatus.ACTIVE,
                    ),
                }))
                .filter((group) => group.competencies.length > 0),
        [groups],
    );
    const assignableCompetencyIds = useMemo(
        () =>
            new Set(assignableGroups.flatMap((group) => group.competencies.map((item) => item.id))),
        [assignableGroups],
    );

    const [step, setStep] = useState(1);

    const steps = useMemo<StepperStep[]>(
        () =>
            STEP_KEYS.map((key) => ({
                key,
                label: (
                    <span className={'text-body-sm'}>
                        {t(`competencies.profiles.wizard.${key}`)}
                    </span>
                ),
            })),
        [t],
    );

    const onStepClick = (index: number) => {
        const targetStep = index + 1;

        if (targetStep < step) {
            setStep(targetStep);
        }
    };

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [priorityIds, setPriorityIds] = useState<string[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [showDescriptionInput, setShowDescriptionInput] = useState(false);

    const navigateToOrigin = () => {
        if (
            embedded &&
            wizardState?.returnMode === CreateProfileReturnMode.ColleaguePerformanceProfile
        ) {
            history.push(
                wizardState.returnEmployeeId
                    ? RouteName.PerformanceProfile.replace(':id', wizardState.returnEmployeeId)
                    : `${RouteName.Competencies}?tab=profiles`,
            );
            return;
        }

        navigate({ path: resolveCreateProfileWizardReturnPath(wizardState) });
    };

    // Creating the profile also assigns it, so the caller's task is done: hand the user back to
    // the module that sent them here. Going back without creating still returns to the origin.
    const goToDestinationAfterCreate = () => {
        if (wizardState?.returnPath) {
            navigate({ path: wizardState.returnPath });
            return;
        }

        navigateToOrigin();
    };

    const setSelection = (ids: string[]) => {
        const assignableIds = ids.filter((id) => assignableCompetencyIds.has(id));
        setSelectedIds(assignableIds);
        setPriorityIds((current) => current.filter((id) => assignableIds.includes(id)));
    };

    const toggleCompetency = (id: string) =>
        setSelection(
            selectedIds.includes(id)
                ? selectedIds.filter((value) => value !== id)
                : [...selectedIds, id],
        );

    const toggleGroupSelection = (group: CompetencyGroup) => {
        const ids = group.competencies.map((competency) => competency.id);
        const allSelected = ids.every((id) => selectedIds.includes(id));

        setSelection(
            allSelected
                ? selectedIds.filter((id) => !ids.includes(id))
                : Array.from(new Set([...selectedIds, ...ids])),
        );
    };

    const togglePriority = (id: string) =>
        setPriorityIds((current) =>
            current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
        );

    const toggleGroupExpanded = (groupId: string) =>
        setExpandedGroups((current) =>
            current.includes(groupId)
                ? current.filter((value) => value !== groupId)
                : [...current, groupId],
        );

    const selectedByGroup = useMemo(
        () =>
            assignableGroups
                .map((group) => ({
                    group,
                    competencies: group.competencies.filter((competency) =>
                        selectedIds.includes(competency.id),
                    ),
                }))
                .filter((entry) => entry.competencies.length > 0),
        [assignableGroups, selectedIds],
    );

    const canContinue =
        (step === 1 && selectedIds.length > 0) || (step === 2 && name.trim().length > 0);

    const goNext = () => {
        if (!canContinue) {
            return;
        }

        if (step === 1) {
            setStep(2);

            return;
        }

        void onCreate();
    };

    const onCreate = async () => {
        const assignmentCompetencyIds = selectedIds.filter((id) => assignableCompetencyIds.has(id));

        if (
            assignmentCompetencyIds.length !== selectedIds.length ||
            assignmentCompetencyIds.length < 1
        ) {
            setSelection(assignmentCompetencyIds);
            setStep(1);
            showErrorToast({ message: t('competencies.actions.competencyDeactivatedTooltip') });
            return;
        }

        try {
            const created = await createProfile({
                name: buildLocalizedRecord(language, name.trim()),
                description: buildLocalizedRecord(language, description.trim()),
                sortOrder: 0,
                items: assignmentCompetencyIds.map((id) => ({
                    competencyId: id,
                    expectedLevel: ProficiencyLevel.SATISFACTORY,
                    isPriority: priorityIds.includes(id),
                })),
            });

            if (returnEmployeeId && created?.id) {
                await assignProfile({
                    profileId: created.id,
                    payload: { employeeIds: [returnEmployeeId] },
                });
            }

            showSuccessToast({ message: t('competencies.profiles.toast.created') });
            goToDestinationAfterCreate();
        } catch (error) {
            const refusal = readRefusal(error);
            const isDuplicateNameError =
                refusal?.status === 400 &&
                Object.values(refusal.fieldErrors)
                    .flat()
                    .some(
                        (message) =>
                            typeof message === 'string' &&
                            message.toLowerCase().includes('profile with this name already exists'),
                    );

            handleError({
                error,
                data: isDuplicateNameError
                    ? {
                          message: t('competencies.profiles.toast.duplicateName'),
                      }
                    : undefined,
            });
        }
    };

    const goBack = () => {
        if (step === 1) {
            navigateToOrigin();

            return;
        }

        setStep((current) => current - 1);
    };

    return (
        <main className={'max-w-competencies mx-auto px-4 pt-6 pb-12'}>
            <BackLink
                label={t('competencies.profiles.wizard.pageBack')}
                onClick={goBack}
                className={'self-start'}
            />

            <h1 className={'text-navy text-heading my-8 font-medium'}>
                {t('competencies.profiles.wizard.creating')}
            </h1>

            <div className={'rounded-xl border border-white bg-white/45 p-8 backdrop-blur-sm'}>
                <Stepper
                    steps={steps}
                    current={step}
                    size={'lg'}
                    onStepClick={onStepClick}
                    className={'mx-auto mb-14 max-w-md'}
                    dataTestId={'wizard-stepper'}
                />

                <div className={'min-h-[360px]'}>
                    {step === 1 && (
                        <div>
                            <div
                                className={
                                    'ds-mb-2xl flex items-center justify-between max-md:flex-wrap'
                                }
                            >
                                <h2 className={'text-navy text-subtitle font-semibold'}>
                                    {t('competencies.profiles.wizard.selectTitle')}
                                </h2>

                                <span className={'text-navy/60 text-body-sm'}>
                                    {t('competencies.profiles.wizard.selectedCount', {
                                        count: selectedIds.length,
                                    })}
                                </span>
                            </div>

                            <div className={'ds-gap-lg flex flex-col'}>
                                {assignableGroups.map((group) => {
                                    const ids = group.competencies.map((c) => c.id);
                                    const selectedCount = ids.filter((id) =>
                                        selectedIds.includes(id),
                                    ).length;
                                    const isAll = selectedCount === ids.length;
                                    const isPartial = selectedCount > 0 && !isAll;
                                    const isExpanded = expandedGroups.includes(group.id);

                                    return (
                                        <div
                                            key={group.id}
                                            className={
                                                'ds-rounded-sm relative overflow-hidden border border-gray-200 bg-white/60'
                                            }
                                        >
                                            <div
                                                className={
                                                    'ds-gap-lg ds-px-2xl ds-py-lg flex items-center'
                                                }
                                            >
                                                <Checkbox
                                                    checked={isAll}
                                                    indeterminate={isPartial}
                                                    className={
                                                        (isAll || isPartial
                                                            ? ''
                                                            : 'border-primary/60! ') +
                                                        'h-[18px]! w-[18px]! rounded-[6px]! border-[1.5px]!'
                                                    }
                                                    onChange={() => toggleGroupSelection(group)}
                                                    dataTestId={`wizard-group-${group.id}`}
                                                />

                                                <button
                                                    type={'button'}
                                                    onClick={() => toggleGroupExpanded(group.id)}
                                                    className={
                                                        'ds-gap-lg flex flex-1 cursor-pointer items-center text-left max-md:flex-wrap'
                                                    }
                                                >
                                                    <div className={'min-w-0'}>
                                                        <h4 className={'text-navy font-semibold'}>
                                                            {getTextByLanguage(
                                                                group.name,
                                                                language,
                                                            )}
                                                        </h4>

                                                        <p className={'text-navy/60 text-body-sm'}>
                                                            {getTextByLanguage(
                                                                group.description,
                                                                language,
                                                            )}
                                                        </p>
                                                    </div>

                                                    <span
                                                        className={
                                                            'ds-px-md ds-py-xs text-caption flex-shrink-0 rounded-full bg-[#f3e8ff] font-medium text-[#7e22ce]'
                                                        }
                                                    >
                                                        {t(
                                                            'competencies.profiles.competencyCount',
                                                            { count: ids.length },
                                                        )}
                                                    </span>

                                                    {selectedCount > 0 && (
                                                        <span
                                                            className={
                                                                'bg-primary/10 text-primary ds-px-md ds-py-xs text-caption flex-shrink-0 rounded-full font-medium'
                                                            }
                                                        >
                                                            {t(
                                                                'competencies.profiles.wizard.selectedInGroup',
                                                                { count: selectedCount },
                                                            )}
                                                        </span>
                                                    )}

                                                    <span
                                                        className={
                                                            'text-navy flex-shrink-0 max-md:absolute max-md:top-[20px] max-md:right-4'
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronUpIcon
                                                                className={'h-[20px] w-[20px]'}
                                                            />
                                                        ) : (
                                                            <ChevronDownIcon
                                                                className={'h-[20px] w-[20px]'}
                                                            />
                                                        )}
                                                    </span>
                                                </button>
                                            </div>

                                            {isExpanded && (
                                                <div
                                                    className={
                                                        'ds-px-2xl ds-pt-xs ds-pb-lg space-y-[12px]'
                                                    }
                                                >
                                                    {group.competencies.map((competency) => {
                                                        const isSelected = selectedIds.includes(
                                                            competency.id,
                                                        );
                                                        const isPriority = priorityIds.includes(
                                                            competency.id,
                                                        );

                                                        return (
                                                            <div
                                                                key={competency.id}
                                                                role={'button'}
                                                                tabIndex={0}
                                                                onClick={() =>
                                                                    toggleCompetency(competency.id)
                                                                }
                                                                onKeyDown={(e) => {
                                                                    if (
                                                                        e.key === 'Enter' ||
                                                                        e.key === ' '
                                                                    ) {
                                                                        if (e.key === ' ') {
                                                                            e.preventDefault();
                                                                        }
                                                                        toggleCompetency(
                                                                            competency.id,
                                                                        );
                                                                    }
                                                                }}
                                                                className={
                                                                    'group ds-gap-md ds-rounded-sm ds-p-md flex cursor-pointer items-start transition-colors ' +
                                                                    (isSelected
                                                                        ? 'bg-[#eff6ff]'
                                                                        : 'hover:bg-gray-50')
                                                                }
                                                            >
                                                                <span
                                                                    className={'mt-[2px]'}
                                                                    onClick={(e) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        size={'sm'}
                                                                        className={
                                                                            (isSelected
                                                                                ? ''
                                                                                : 'border-primary/60! ') +
                                                                            'h-[18px]! w-[18px]! rounded-[6px]! border-[1.5px]!'
                                                                        }
                                                                        onChange={() =>
                                                                            toggleCompetency(
                                                                                competency.id,
                                                                            )
                                                                        }
                                                                        dataTestId={`wizard-competency-${competency.id}`}
                                                                    />
                                                                </span>

                                                                <div className={'min-w-0 flex-1'}>
                                                                    <div
                                                                        className={
                                                                            'ds-mb-xs ds-gap-sm flex items-center'
                                                                        }
                                                                    >
                                                                        <h5
                                                                            className={
                                                                                'text-navy font-medium'
                                                                            }
                                                                        >
                                                                            {getTextByLanguage(
                                                                                competency.name,
                                                                                language,
                                                                            )}
                                                                        </h5>

                                                                        {isSelected && (
                                                                            <button
                                                                                type={'button'}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    togglePriority(
                                                                                        competency.id,
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    'ds-gap-xs ds-rounded-xs ds-px-sm text-caption flex items-center py-[2px] font-medium transition-all ' +
                                                                                    (isPriority
                                                                                        ? 'bg-primary text-white'
                                                                                        : 'hover:bg-primary/10 hover:text-primary bg-gray-100 text-gray-600 opacity-0 group-hover:opacity-100')
                                                                                }
                                                                            >
                                                                                {isPriority ? (
                                                                                    <svg
                                                                                        className={
                                                                                            'h-[12px] w-[12px]'
                                                                                        }
                                                                                        fill={
                                                                                            'currentColor'
                                                                                        }
                                                                                        viewBox={
                                                                                            '0 0 24 24'
                                                                                        }
                                                                                    >
                                                                                        <path
                                                                                            d={
                                                                                                'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'
                                                                                            }
                                                                                        />
                                                                                    </svg>
                                                                                ) : (
                                                                                    <svg
                                                                                        className={
                                                                                            'h-[12px] w-[12px]'
                                                                                        }
                                                                                        fill={
                                                                                            'none'
                                                                                        }
                                                                                        stroke={
                                                                                            'currentColor'
                                                                                        }
                                                                                        viewBox={
                                                                                            '0 0 24 24'
                                                                                        }
                                                                                    >
                                                                                        <path
                                                                                            strokeLinecap={
                                                                                                'round'
                                                                                            }
                                                                                            strokeLinejoin={
                                                                                                'round'
                                                                                            }
                                                                                            strokeWidth={
                                                                                                2
                                                                                            }
                                                                                            d={
                                                                                                'M5 12l5 5L20 7'
                                                                                            }
                                                                                        />
                                                                                    </svg>
                                                                                )}

                                                                                {isPriority
                                                                                    ? t(
                                                                                          'competencies.profiles.wizard.markedAsPriority',
                                                                                      )
                                                                                    : t(
                                                                                          'competencies.profiles.wizard.markPriority',
                                                                                      )}
                                                                            </button>
                                                                        )}
                                                                    </div>

                                                                    <p
                                                                        className={
                                                                            'text-navy/60 text-body-sm'
                                                                        }
                                                                    >
                                                                        {getTextByLanguage(
                                                                            competency.description,
                                                                            language,
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className={'flex flex-col gap-6'}>
                            {returnEmployeeName && (
                                <div
                                    className={
                                        'ds-gap-md ds-rounded-sm ds-p-lg flex items-start border border-blue-200 bg-blue-50 max-sm:flex-wrap'
                                    }
                                >
                                    <Icon
                                        name={'essential-info-circle'}
                                        color={Colors.INFO}
                                    />

                                    <p className={'text-body-sm text-blue-800'}>
                                        {t('competencies.profiles.wizard.assignNotice', {
                                            name: returnEmployeeName,
                                        })}
                                    </p>
                                </div>
                            )}

                            <div>
                                <h2 className={'text-navy ds-mb-sm text-subtitle font-semibold'}>
                                    {t('competencies.profiles.wizard.reviewTitle')}
                                </h2>

                                <p className={'text-navy/60 text-body-sm'}>
                                    {t('competencies.profiles.wizard.reviewSubtitle')}
                                </p>
                            </div>

                            <div
                                className={
                                    'ds-rounded-sm ds-p-2xl border border-gray-200 bg-white/60'
                                }
                            >
                                <div className={'ds-mb-lg flex flex-col gap-2'}>
                                    <div className={'flex items-center gap-2'}>
                                        <label className={'text-navy text-body-sm font-semibold'}>
                                            {t('competencyProfiles.createWizard.review.nameLabel')}

                                            <span className={'text-error ml-0.5'}>*</span>
                                        </label>

                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild={true}>
                                                    <span className={'inline-flex'}>
                                                        <Icon
                                                            name={'essential-info-circle'}
                                                            size={'sm'}
                                                            colorClassName={
                                                                'text-base-400 hover:text-navy transition-colors'
                                                            }
                                                            className={'cursor-pointer'}
                                                            ariaLabel={t(
                                                                'competencyProfiles.createWizard.review.nameTooltip',
                                                            )}
                                                        />
                                                    </span>
                                                </TooltipTrigger>

                                                <TooltipContent
                                                    side={'right'}
                                                    className={'max-w-xs'}
                                                >
                                                    <p>
                                                        {t(
                                                            'competencyProfiles.createWizard.review.nameTooltip',
                                                        )}
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>

                                    <TextInput
                                        value={name}
                                        onChange={(value) => setName(value)}
                                        placeholder={t(
                                            'competencyProfiles.createWizard.review.namePlaceholder',
                                        )}
                                        dataTestId={'wizard-name-input'}
                                        maxLength={NAME_MAX_LENGTH}
                                        variant={'white'}
                                        className={
                                            'focus-within:ring-primary text-navy border-slate-200 focus-within:border-slate-200 focus-within:ring-2 focus-within:ring-offset-2'
                                        }
                                    />
                                    <div className={'flex justify-end'}>
                                        <CharacterCount
                                            value={name}
                                            maxLength={NAME_MAX_LENGTH}
                                        />
                                    </div>
                                </div>

                                {showDescriptionInput ? (
                                    <>
                                        <div
                                            className={
                                                'flex flex-col gap-2 md:flex-row md:items-center'
                                            }
                                        >
                                            <label
                                                className={
                                                    'text-body-sm font-semibold whitespace-nowrap text-slate-500'
                                                }
                                            >
                                                {t(
                                                    'competencyProfiles.createWizard.review.descriptionLabel',
                                                )}
                                            </label>

                                            <div className={'w-full md:flex-1'}>
                                                <MultilineInput
                                                    value={description}
                                                    onChange={(value) => setDescription(value)}
                                                    placeholder={t(
                                                        'competencyProfiles.createWizard.review.descriptionPlaceholder',
                                                    )}
                                                    rows={1}
                                                    autoGrow={true}
                                                    maxLength={DESCRIPTION_MAX_LENGTH}
                                                    variant={'white'}
                                                    className={
                                                        'focus-within:ring-primary/20 text-navy border-slate-200 focus-within:border-slate-200 focus-within:ring-2'
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className={'mt-2 flex justify-end'}>
                                            <CharacterCount
                                                value={description}
                                                maxLength={DESCRIPTION_MAX_LENGTH}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <button
                                        type={'button'}
                                        onClick={() => setShowDescriptionInput(true)}
                                        className={
                                            'text-primary hover:text-primary/80 text-body-sm font-medium transition-colors'
                                        }
                                        data-testid={'wizard-description-toggle'}
                                    >
                                        {t('competencyProfiles.createWizard.review.addDescription')}
                                    </button>
                                )}
                            </div>

                            <div className={'flex flex-col gap-2'}>
                                {selectedByGroup.map(({ group, competencies }) => (
                                    <div key={group.id}>
                                        <h4
                                            className={
                                                'text-navy text-body-sm mt-2 mb-2 font-semibold'
                                            }
                                        >
                                            {getTextByLanguage(group.name, language)}
                                        </h4>

                                        <div className={'flex flex-col gap-1.5'}>
                                            {competencies.map((competency) => (
                                                <div
                                                    key={competency.id}
                                                    className={
                                                        'flex items-center gap-2 rounded-lg border border-gray-200 bg-white/60 px-3 py-2 text-sm'
                                                    }
                                                >
                                                    <span className={'text-navy flex-1 truncate'}>
                                                        {getTextByLanguage(
                                                            competency.name,
                                                            language,
                                                        )}
                                                    </span>

                                                    {priorityIds.includes(competency.id) && (
                                                        <span
                                                            className={
                                                                'bg-primary flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-white'
                                                            }
                                                        >
                                                            <svg
                                                                className={'h-3 w-3'}
                                                                fill={'currentColor'}
                                                                viewBox={'0 0 24 24'}
                                                            >
                                                                <path
                                                                    d={
                                                                        'M11.48 3.5l2.2 4.46 4.92.72-3.56 3.47.84 4.9-4.4-2.31-4.4 2.31.84-4.9L4.4 8.68l4.92-.72 2.16-4.46z'
                                                                    }
                                                                />
                                                            </svg>

                                                            {t('competencies.profiles.priority')}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div
                    className={
                        'mt-8 flex items-center justify-between border-t border-gray-200 pt-6'
                    }
                >
                    {step > 1 ? (
                        <button
                            type={'button'}
                            onClick={goBack}
                            className={
                                'text-navy flex h-11 items-center rounded-lg border border-gray-300 bg-white px-6 text-sm font-medium transition-colors hover:bg-[#f5f7f9]'
                            }
                        >
                            {t('competencies.profiles.wizard.previous')}
                        </button>
                    ) : (
                        <div />
                    )}

                    <ButtonGradientPrimary
                        size={'md'}
                        onClick={goNext}
                        disabled={!canContinue || isPending}
                        dataTestId={'wizard-next'}
                    >
                        {step === STEP_KEYS.length
                            ? t('competencies.profiles.wizard.finish')
                            : t('competencies.profiles.wizard.next')}
                    </ButtonGradientPrimary>
                </div>
            </div>
        </main>
    );
};
